import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import useT from '@hooks/useT';
import AdminThemeWrapper from '@templates/AdminThemeWrapper';
import { selectIsAdminZone } from '@store/ThemeSlice';

import {ModalContext} from './ModalContext';

function useSafeIsAdminZone(): boolean {
    try {
        return useSelector(selectIsAdminZone);
    } catch {
        return false;
    }
}

const Modal = () => {
    const { t } = useT();
    const {
        open,
        content,
        fullscreen,
        closeModal,
        requestClose,
        showCloseConfirmation,
        cancelCloseConfirmation,
    } = React.useContext(ModalContext);
    const isAdminZone = useSafeIsAdminZone();

    const handleClose = (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            requestClose();
        }
    };

    const handleConfirmClose = () => {
        cancelCloseConfirmation();
        closeModal();
    };

    const handleCancelClose = () => {
        cancelCloseConfirmation();
    };

    const confirmationDialog = (
        <Dialog
            open={showCloseConfirmation}
            onClose={handleCancelClose}
            sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
            <DialogTitle>{t('modal.unsavedChanges.title')}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {t('modal.unsavedChanges.message')}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancelClose} color="primary">
                    {t('modal.unsavedChanges.cancel')}
                </Button>
                <Button onClick={handleConfirmClose} color="error" variant="contained">
                    {t('modal.unsavedChanges.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );

    const dialog = (
        <>
            <Dialog
                open={open}
                fullWidth
                maxWidth="md"
                scroll="paper"
                fullScreen={fullscreen}
                onClose={handleClose}
                disableEnforceFocus
                sx={{overflowX: 'hidden'}}>
                {content}
            </Dialog>
            {confirmationDialog}
        </>
    );

    // Wrap entire Dialog with AdminThemeWrapper when in admin zone
    // This ensures the Dialog backdrop and paper also get dark theme
    return isAdminZone ? <AdminThemeWrapper>{dialog}</AdminThemeWrapper> : dialog;
};

Modal.displayName = 'Modal';

export default Modal;
